import React, { useState, useEffect } from 'react';
import { ShoppingBag, Plus, X, Save, Eye, Calendar, Truck, FileText, Upload, Image as ImageIcon } from 'lucide-react';
import { API_URL } from '../../services/api';
import { notifySuccess, notifyError } from '../../utils/notifications';

function Achats() {
  const [achats, setAchats] = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAchat, setSelectedAchat] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    fournisseur_id: '',
    date_commande: new Date().toISOString().split('T')[0],
    date_livraison_prevue: '',
    mode_paiement: 'virement',
    notes: '',
    lignes: [],
    facture_image: null,
    facture_image_preview: null,
    montant_ht: '',
    tva_pourcentage: 20,
    montant_tva: '',
    montant_ttc: ''
  });

  const [nouvelleLigne, setNouvelleLigne] = useState({
    type_matiere: '',
    designation: '',
    quantite: 1,
    prix_unitaire: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await fetch(`${API_URL}/fournisseurs`);
      const data = await response.json();
      setFournisseurs(Array.isArray(data) ? data : []);

      const achatsRes = await fetch(`${API_URL}/stock-matieres/stock`);
      const achatsData = await achatsRes.json();
      
      const sorted = Array.isArray(achatsData) 
        ? achatsData.sort((a, b) => new Date(b.date_achat) - new Date(a.date_achat))
        : [];
      
      setAchats(sorted);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setFournisseurs([]);
      setAchats([]);
    }
  };

  const ajouterLigne = () => {
    const prix = parseFloat(nouvelleLigne.prix_unitaire);
    const qte = parseFloat(nouvelleLigne.quantite);
    
    if (!nouvelleLigne.type_matiere || !nouvelleLigne.designation || qte <= 0 || isNaN(prix) || prix <= 0) {
      alert('Veuillez remplir tous les champs de la ligne correctement');
      return;
    }
    
    setFormData({
      ...formData,
      lignes: [...formData.lignes, {
        ...nouvelleLigne,
        quantite: qte,
        prix_unitaire: prix,
        prix_total: qte * prix
      }]
    });

    setNouvelleLigne({
      type_matiere: '',
      designation: '',
      quantite: 1,
      prix_unitaire: ''
    });
  };

  const supprimerLigne = (index) => {
    setFormData({
      ...formData,
      lignes: formData.lignes.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.fournisseur_id) {
      alert('Veuillez sélectionner un fournisseur');
      return;
    }

    let lignesToSave = [...formData.lignes];
    
    if (lignesToSave.length === 0 && nouvelleLigne.type_matiere && nouvelleLigne.designation) {
      const prix = parseFloat(nouvelleLigne.prix_unitaire);
      const qte = parseFloat(nouvelleLigne.quantite);
      
      if (qte > 0 && !isNaN(prix) && prix > 0) {
        lignesToSave.push({
          ...nouvelleLigne,
          quantite: qte,
          prix_unitaire: prix,
          prix_total: qte * prix
        });
      }
    }

    if (lignesToSave.length === 0) {
      alert('Veuillez remplir au moins un article');
      return;
    }

    setSaving(true);
    try {
      for (const ligne of lignesToSave) {
        const reference = ligne.designation.match(/[A-Z0-9]+-[A-Z0-9]+/i) 
          ? ligne.designation 
          : `${formData.fournisseur_id}-${ligne.type_matiere}-${Date.now()}`;
        
        await fetch(`${API_URL}/stock-matieres/stock`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            reference: reference,
            categorie: ['OR 18K', 'OR 14K', 'ARGENT'].includes(ligne.type_matiere) ? 'METAL' : 'PIERRE',
            type_matiere: ligne.type_matiere,
            designation: ligne.designation,
            quantite: ligne.quantite,
            unite: ['DIAMANT', 'RUBIS', 'EMERAUDE', 'SAPHIR'].includes(ligne.type_matiere) ? 'carat' : 'g',
            fournisseur_id: formData.fournisseur_id,
            prix_achat_unitaire: ligne.prix_unitaire,
            prix_achat_total: ligne.prix_total,
            date_achat: formData.date_commande,
            notes: formData.notes
          })
        });
      }
      
      notifySuccess('Achat enregistre');
      setShowModal(false);
      resetForm();
      await loadData();
    } catch (error) {
      console.error('Erreur:', error);
      notifyError('Erreur enregistrement achat : ' + (error?.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        facture_image: file,
        facture_image_preview: URL.createObjectURL(file)
      });
    }
  };

  const calculerMontants = (ht, tva) => {
    const montantHT = parseFloat(ht) || 0;
    const tauxTVA = parseFloat(tva) || 0;
    const montantTVA = (montantHT * tauxTVA) / 100;
    const montantTTC = montantHT + montantTVA;
    
    setFormData({
      ...formData,
      montant_ht: ht,
      tva_pourcentage: tva,
      montant_tva: montantTVA.toFixed(2),
      montant_ttc: montantTTC.toFixed(2)
    });
  };

  const resetForm = () => {
    setFormData({
      fournisseur_id: '',
      date_commande: new Date().toISOString().split('T')[0],
      date_livraison_prevue: '',
      mode_paiement: 'virement',
      notes: '',
      lignes: [],
      facture_image: null,
      facture_image_preview: null,
      montant_ht: '',
      tva_pourcentage: 20,
      montant_tva: '',
      montant_ttc: ''
    });
    setNouvelleLigne({
      type_matiere: '',
      designation: '',
      quantite: 1,
      prix_unitaire: ''
    });
  };

  const handleViewDetails = (achat) => {
    setSelectedAchat(achat);
    setShowDetailModal(true);
  };

  const montantTotal = formData.lignes.reduce((sum, ligne) => sum + (ligne.quantite * ligne.prix_unitaire), 0);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-gray-800 flex items-center space-x-3">
          <ShoppingBag className="h-8 w-8 text-indigo-600" />
          <span>Gestion des Achats</span>
        </h2>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all flex items-center space-x-2 shadow-lg"
        >
          <Plus className="h-5 w-5" />
          <span>Nouvelle commande</span>
        </button>
      </div>

      {/* Liste des achats */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">N° Commande</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fournisseur</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Montant</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {achats.map((achat) => {
              const fournisseur = fournisseurs.find(f => f.id === achat.fournisseur_id);
              return (
                <tr key={achat.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <FileText className="h-4 w-4 text-indigo-600" />
                      <span className="font-semibold text-gray-900">{achat.reference}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span>{achat.date_achat ? new Date(achat.date_achat).toLocaleDateString('fr-FR') : '-'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <Truck className="h-4 w-4 text-blue-600" />
                      <span className="font-medium text-gray-900">{fournisseur?.nom || 'N/A'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-right">
                    <span className="font-bold text-indigo-600 text-lg">
                      {parseFloat(achat.prix_achat_total || 0).toLocaleString()} DH
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-center">
                    <button
                      onClick={() => handleViewDetails(achat)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Voir détails"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal de création */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-4 flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <ShoppingBag className="h-6 w-6" />
                <h3 className="text-xl font-bold">Nouvelle commande fournisseur</h3>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fournisseur *</label>
                  <select
                    value={formData.fournisseur_id}
                    onChange={(e) => setFormData({...formData, fournisseur_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="">Sélectionner un fournisseur</option>
                    {fournisseurs.map(f => (
                      <option key={f.id} value={f.id}>{f.nom}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de commande</label>
                  <input
                    type="date"
                    value={formData.date_commande}
                    onChange={(e) => setFormData({...formData, date_commande: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de livraison prévue</label>
                  <input
                    type="date"
                    value={formData.date_livraison_prevue}
                    onChange={(e) => setFormData({...formData, date_livraison_prevue: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mode de paiement</label>
                  <select
                    value={formData.mode_paiement}
                    onChange={(e) => setFormData({...formData, mode_paiement: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="virement">Virement</option>
                    <option value="cheque">Chèque</option>
                    <option value="especes">Espèces</option>
                    <option value="carte">Carte</option>
                    <option value="credit">Crédit</option>
                  </select>
                </div>
              </div>

              {/* Section Facture et Montants */}
              <div className="border-t pt-4 space-y-4">
                <h4 className="text-lg font-bold text-gray-800 flex items-center space-x-2">
                  <FileText className="h-5 w-5 text-indigo-600" />
                  <span>Informations de facturation</span>
                </h4>

                <div className="grid grid-cols-2 gap-4">
                  {/* Upload de facture */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Image de la facture (optionnel)
                    </label>
                    <div className="flex items-center space-x-4">
                      <label className="flex-1 flex flex-col items-center px-4 py-6 bg-white border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-indigo-500 transition-colors">
                        <Upload className="h-8 w-8 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">Cliquez pour uploader une facture</span>
                        <span className="text-xs text-gray-500 mt-1">PNG, JPG, PDF jusqu'à 10MB</span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*,.pdf"
                          onChange={handleImageUpload}
                        />
                      </label>
                      
                      {formData.facture_image_preview && (
                        <div className="relative">
                          <img
                            src={formData.facture_image_preview}
                            alt="Aperçu facture"
                            className="h-32 w-32 object-cover rounded-lg border-2 border-indigo-500"
                          />
                          <button
                            type="button"
                            onClick={() => setFormData({...formData, facture_image: null, facture_image_preview: null})}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Montants HT/TVA/TTC */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Montant HT</label>
                    <div className="relative">
                      <input
                        type="text" inputMode="decimal" onFocus={e => e.target.select()}
                        step="0.01"
                        value={formData.montant_ht}
                        onChange={(e) => calculerMontants(e.target.value, formData.tva_pourcentage)}
                        className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        placeholder="0.00"
                      />
                      <span className="absolute right-3 top-2.5 text-gray-500 font-semibold">DH</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">TVA (%)</label>
                    <select
                      value={formData.tva_pourcentage}
                      onChange={(e) => calculerMontants(formData.montant_ht, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="0">0%</option>
                      <option value="7">7%</option>
                      <option value="10">10%</option>
                      <option value="14">14%</option>
                      <option value="20">20%</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Montant TVA</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.montant_tva}
                        readOnly
                        className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg bg-gray-50"
                        placeholder="0.00"
                      />
                      <span className="absolute right-3 top-2.5 text-gray-500 font-semibold">DH</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Montant TTC</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.montant_ttc}
                        readOnly
                        className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 font-bold text-green-700"
                        placeholder="0.00"
                      />
                      <span className="absolute right-3 top-2.5 text-green-700 font-bold">DH</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-lg font-bold text-gray-800 mb-3">Ajouter un article</h4>
                <div className="grid grid-cols-6 gap-3">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type de matière *</label>
                    <select
                      value={nouvelleLigne.type_matiere || ''}
                      onChange={(e) => setNouvelleLigne({...nouvelleLigne, type_matiere: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Sélectionner un type</option>
                      <option value="DIAMANT">💎 Diamant</option>
                      <option value="OR 18K">🟡 Or 18K</option>
                      <option value="ARGENT">⚪ Argent</option>
                      <option value="RUBIS">🔴 Rubis</option>
                      <option value="EMERAUDE">🟢 Émeraude</option>
                      <option value="SAPHIR">🔵 Saphir</option>
                    </select>
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Désignation *</label>
                    <input
                      type="text"
                      value={nouvelleLigne.designation}
                      onChange={(e) => setNouvelleLigne({...nouvelleLigne, designation: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="Ex: DIA-0009"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantité *</label>
                    <input
                      type="text" inputMode="decimal" onFocus={e => e.target.select()}
                      value={nouvelleLigne.quantite}
                      onChange={(e) => setNouvelleLigne({...nouvelleLigne, quantite: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      min="1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prix unitaire *</label>
                    <input
                      type="text" inputMode="decimal" onFocus={e => e.target.select()}
                      step="0.01"
                      value={nouvelleLigne.prix_unitaire}
                      onChange={(e) => setNouvelleLigne({...nouvelleLigne, prix_unitaire: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      min="0"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {formData.lignes.length > 0 && (
                  <div className="mt-4">
                    <h5 className="font-semibold mb-2">Articles ajoutés :</h5>
                    {formData.lignes.map((ligne, index) => (
                      <div key={index} className="flex justify-between items-center bg-gray-50 p-2 rounded mb-2">
                        <span>{ligne.designation} - {ligne.quantite} x {ligne.prix_unitaire} DH</span>
                        <button
                          type="button"
                          onClick={() => supprimerLigne(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between mt-3">
                  <button
                    type="button"
                    onClick={ajouterLigne}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Ajouter la ligne</span>
                  </button>
                  
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-3 rounded-lg border-2 border-orange-200">
                    <div className="text-sm text-gray-600">Total de la commande</div>
                    <div className="text-2xl font-bold text-orange-600">
                      {(montantTotal + (nouvelleLigne.quantite * parseFloat(nouvelleLigne.prix_unitaire || 0))).toFixed(2)} DH
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 flex items-center space-x-2 disabled:opacity-50"
                >
                  <Save className="h-5 w-5" />
                  <span>{saving ? 'Enregistrement...' : 'Créer la commande'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de détails */}
      {showDetailModal && selectedAchat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 flex justify-between items-center rounded-t-lg">
              <div className="flex items-center space-x-3">
                <Eye className="h-6 w-6" />
                <h3 className="text-xl font-bold">Détails de l'achat</h3>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-600">Référence</label>
                  <div className="font-semibold text-gray-900">{selectedAchat.reference}</div>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Date d'achat</label>
                  <div className="font-semibold text-gray-900">
                    {selectedAchat.date_achat ? new Date(selectedAchat.date_achat).toLocaleDateString('fr-FR') : '-'}
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Fournisseur</label>
                  <div className="font-semibold text-gray-900">
                    {fournisseurs.find(f => f.id === selectedAchat.fournisseur_id)?.nom || 'N/A'}
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Type de matière</label>
                  <div className="font-semibold text-gray-900">{selectedAchat.type_matiere || '-'}</div>
                </div>
                <div className="col-span-2">
                  <label className="text-sm text-gray-600">Désignation</label>
                  <div className="font-semibold text-gray-900">{selectedAchat.designation || '-'}</div>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Quantité</label>
                  <div className="font-semibold text-gray-900">
                    {selectedAchat.quantite} {selectedAchat.unite || 'unité(s)'}
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Prix unitaire</label>
                  <div className="font-semibold text-gray-900">
                    {parseFloat(selectedAchat.prix_achat_unitaire || 0).toFixed(2)} DH
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-700">Prix total</span>
                    <span className="text-2xl font-bold text-indigo-600">
                      {parseFloat(selectedAchat.prix_achat_total || 0).toFixed(2)} DH
                    </span>
                  </div>
                </div>
              </div>

              {selectedAchat.notes && (
                <div>
                  <label className="text-sm text-gray-600">Notes</label>
                  <div className="text-gray-900 bg-gray-50 p-3 rounded-lg">{selectedAchat.notes}</div>
                </div>
              )}

              <div className="flex justify-end pt-4 border-t">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Achats;
